import { Question } from "../../../../../api/src/types";
import config from "../../../config";
import { addDays, isAfter, isBefore, parseISO, startOfDay } from "date-fns";
import { validatePhoneNumber } from "../../../utils/sharedValidators";

export const validateQuestionnaireDate = (
  date: string,
  question: Question,
  stateAbbreviation?: string,
): string | null => {
  if (
    question.id === config.weddingDateQuestionId &&
    stateAbbreviation === "CA"
  ) {
    const sevenDaysFromToday = addDays(startOfDay(new Date()), 7);
    const selectedDate = parseISO(date);
    if (isBefore(selectedDate, sevenDaysFromToday)) {
      return "In CA you must review the document for 7 days before notarizing";
    }
  }
  // Check if required field is empty
  if (question.isRequired && (!date || date.trim() === "")) {
    return "Date is required";
  }

  // If empty but not required, allow it
  if (!date || date.trim() === "") {
    return null;
  }

  // Check if it's a valid date
  // parseISO correctly handles YYYY-MM-DD format from HTML date inputs as local dates
  const dateValue = parseISO(date);
  const today = startOfDay(new Date());
  if (isNaN(dateValue.getTime())) {
    return "Please enter a valid date";
  }

  // Apply input restrictions
  if (question.inputRestriction === "FUTURE_DATE") {
    if (isBefore(dateValue, today)) {
      return "Date should be in the future";
    }
  }

  if (question.inputRestriction === "PAST_DATE") {
    if (/birth/i.exec(question.title)) {
      const eighteenYearsAgo = new Date();
      eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18);
      const eighteenYearsAgoStart = startOfDay(eighteenYearsAgo);
      if (isAfter(dateValue, eighteenYearsAgoStart)) {
        return "You must be at least 18 years old";
      }
    }

    if (!isBefore(dateValue, today)) {
      return "Date should be in the past";
    }
  }

  return null;
};

export const validateQuestionnaireNumber = (
  value: string,
  question: Question,
): string | null => {
  // Check if required field is empty
  if (question.isRequired && (!value || value.trim() === "")) {
    return "This field is required";
  }

  // If empty but not required, allow it
  if (!value || value.trim() === "") {
    return null;
  }

  // Check if it's a valid number
  if (isNaN(Number(value))) {
    return "Value must be a number";
  }

  // Apply input restrictions
  if (question.inputRestriction === "GREATER_THAN_ZERO") {
    if (Number(value) <= 0) {
      return "Value should be greater than zero";
    }
  }

  return null;
};

export const validateCurrencyWithRestriction = (
  value: string,
  question: Question,
): string | null => {
  // Basic currency validation first
  if (!value) {
    if (question.isRequired) {
      return "Currency value is required";
    }
    return null;
  }

  // Remove currency symbols and parse as number for validation
  const numericValue = parseFloat(value.replace(/[$,]/g, ""));

  if (isNaN(numericValue)) {
    return "Invalid currency value";
  }

  // Apply input restrictions
  if (question.inputRestriction === "GREATER_THAN_ZERO") {
    if (numericValue <= 0) {
      return "Value should be greater than zero";
    }
  }

  return null;
};

export const validatePhoneWithRestriction = (
  value: string,
  question: Question,
): string | null => {
  // Check if required field is empty
  if (question.isRequired && (!value || value.trim() === "")) {
    return "Phone number is required";
  }

  // If empty but not required, allow it
  if (!value || value.trim() === "") {
    return null;
  }

  // Import and use the basic phone validation
  return validatePhoneNumber(value);
};

export const validateTextWithRestriction = (
  value: string,
  question: Question,
): string | null => {
  if (question.isRequired && (!value || value.trim() === "")) {
    return "This field is required";
  }

  // Text fields don't typically use the current input restrictions
  // but we can add basic validation here if needed
  return null;
};
